import { useContext, useState } from "react";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";

import type { S132Period } from "@thalamos/aspire-common";
import { isS132RecordingEnabled } from "@thalamos/aspire-common";

import { S132HistoryDialogContainer } from "./S132HistoryDialogContainer.js";
import {
  s132DutiesProvision,
  s132DutiesText,
  s132DutyProvision,
  s132DutyText,
  s132PeriodStatusText,
  s132StatusFill,
} from "./S132StatusPill.js";
import { apiHooks } from "~/api.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";

export type S132BannerCardProps = {
  /** The current periods that owe the information. */
  periods: S132Period[];
  fullWidth?: boolean;
  onOpen: () => void;
};

/**
 * Matches the MHA status card's shape so the two read as one row of facts
 * about the detention. The whole card opens the record.
 */
export function S132BannerCard(props: S132BannerCardProps) {
  const theme = useTheme();
  const showSection = props.periods.length > 1;
  const duties = props.periods.map((p) => p.duty);
  const dutiesDiffer = new Set(duties).size > 1;

  return (
    <ButtonBase
      data-testid="s132-banner-status"
      aria-label={`Open ${s132DutiesProvision(duties)} information: ${props.periods
        .map(
          (p) =>
            `${p.section}${dutiesDiffer ? ` (${s132DutyProvision(p.duty)})` : ""} ${s132PeriodStatusText(p)}`,
        )
        .join(", ")}`}
      onClick={props.onOpen}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        textAlign: "left",
        backgroundColor: "white",
        borderRadius: theme.spacing(0.3),
        paddingX: theme.spacing(2.5),
        paddingY: theme.spacing(1.5),
        width: props.fullWidth ? "100%" : "fit-content",
        minWidth: "13rem",
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          alignSelf: "flex-end",
          textDecoration: "underline",
          fontSize: "9px",
          color: theme.palette.primary.main,
          paddingBottom: theme.spacing(1),
        }}
      >
        View record
      </Typography>
      <Typography sx={{ fontSize: "11px", lineHeight: "13px" }}>
        {`${s132DutiesText(duties)} Information`}
      </Typography>
      <Box sx={{ display: "grid", gap: "2px", marginTop: "3px" }}>
        {props.periods.map((period) => (
          <Box
            key={`${period.section}-${period.start}`}
            sx={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Box
              component="span"
              sx={{
                width: 9,
                height: 9,
                flex: "none",
                borderRadius: "50%",
                backgroundColor: s132StatusFill(period.status),
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                lineHeight: "22px",
                color: theme.palette.common.blueGray,
              }}
            >
              {`${showSection ? `${period.section}${dutiesDiffer ? ` (${s132DutyText(period.duty)})` : ""}: ` : ""}${s132PeriodStatusText(period)}`}
            </Typography>
          </Box>
        ))}
      </Box>
    </ButtonBase>
  );
}

export type S132BannerStatusProps = {
  patientId: string;
  patientName?: string;
};

/**
 * Shows the status of each current section where the organisation records
 * Section 132 information. The live record is mounted only when recording is
 * enabled, so an organisation without it holds no subscription either.
 */
export function S132BannerStatus(props: S132BannerStatusProps) {
  const userContext = useContext(LoggedInUserContext);
  if (!isS132RecordingEnabled(userContext?.user)) return null;
  return <LiveS132BannerStatus {...props} />;
}

function LiveS132BannerStatus(props: S132BannerStatusProps) {
  const { isMobileView } = useScreenDetection();
  const [{ data: record }, refetch] = apiHooks.s132.liveRecord(props.patientId);
  const [open, setOpen] = useState(false);

  const current = (record?.periods ?? []).filter((p) => p.current);
  if (current.length === 0) return null;

  return (
    <>
      <S132BannerCard
        periods={current}
        fullWidth={isMobileView}
        onOpen={() => {
          setOpen(true);
        }}
      />
      {open && (
        <S132HistoryDialogContainer
          patientId={props.patientId}
          patientName={props.patientName}
          onClose={() => {
            setOpen(false);
            refetch();
          }}
        />
      )}
    </>
  );
}
